import type { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ReactCountryFlag from 'react-country-flag'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { installationCreateGoodsFamilyValues } from '@/features/installations/components/installation-create/constants'
import { InstallationFormMessage } from '@/features/installations/components/installation-form-message'
import type { InstallationCreateFormInput } from '@/features/installations/installation-create.schema'

type InstallationCreateStepGeneralProps = {
  form: UseFormReturn<InstallationCreateFormInput>
  requiresLocalNameAndAddress: boolean
  placeOfRegistration: string
}

export function InstallationCreateStepGeneral({
  form,
  requiresLocalNameAndAddress,
  placeOfRegistration,
}: InstallationCreateStepGeneralProps) {
  const { t } = useTranslation('installations')

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('create.sections.general')}</CardTitle>
        <CardDescription>{t('create.wizard.steps.general.description')}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('create.fields.name.label')}</FormLabel>
              <FormControl>
                <Input
                  autoComplete="organization"
                  {...field}
                  onFocus={() => {
                    form.clearErrors('name')
                  }}
                />
              </FormControl>
              <InstallationFormMessage />
            </FormItem>
          )}
        />
        {requiresLocalNameAndAddress ? (
          <FormField
            control={form.control}
            name="localName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  {t('create.fields.localName.label')}
                  {placeOfRegistration ? (
                    <ReactCountryFlag countryCode={placeOfRegistration} svg />
                  ) : null}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    onFocus={() => {
                      form.clearErrors('localName')
                    }}
                  />
                </FormControl>
                <InstallationFormMessage />
              </FormItem>
            )}
          />
        ) : null}
        <FormField
          control={form.control}
          name="goodsFamily"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('create.fields.goodsFamily.label')}</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value || undefined}
                onOpenChange={(open) => {
                  if (open) {
                    form.clearErrors('goodsFamily')
                  }
                }}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('create.fields.goodsFamily.placeholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {installationCreateGoodsFamilyValues.map((goodsFamily) => (
                    <SelectItem key={goodsFamily} value={goodsFamily}>
                      {t(`create.fields.goodsFamily.options.${goodsFamily}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <InstallationFormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('create.fields.description.label')}</FormLabel>
              <FormControl>
                <textarea
                  rows={4}
                  className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[96px] w-full rounded-md border px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-[3px] focus-visible:outline-1 disabled:cursor-not-allowed disabled:opacity-50"
                  name={field.name}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onFocus={() => {
                    form.clearErrors('description')
                  }}
                />
              </FormControl>
              <InstallationFormMessage />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  )
}
